import { hashQuery, query } from '../query';
import { getStorage } from '../storage';
import { trackError } from '../omega';
import { utils } from '@didi/webx-js-core';
const EventTable = {
    ERROR_CTX_URL_PARAMS_NOT_SAME: {
        label: 'SSR 渲染上下文的 URL 参数不一致',
        // Note: 请勿随意更改 ssrErrorType，该字段与 MAS 上 SSR 监控报警策略存在关联。如需修改，请同步@huoyinghui、@huwenkang。
        ssrErrorType: 'url_params_not_same',
    },
    ERROR_CTX_SAME_USER_UID_NOT_SAME: {
        label: 'SSR 渲染上下文同一用户 uid 不一致',
        ssrErrorType: 'uid_not_same',
    },
    ERROR_CTX_SAME_USER_TOKEN_NOT_SAME: {
        label: 'SSR 渲染上下文同一用户 token 不一致',
        ssrErrorType: 'token_not_same',
    },
    ERROR_MISSING_UID: {
        label: '缺少 server 端的 uid 或 web 端的uid',
        ssrErrorType: 'missing_uid'
    },
    ERROR_PLEASE_ENSURE_HAS_LOGIN: {
        label: '请确保调用 checkSSRStatus 前已完成登录',
        ssrErrorType: 'not_login'
    },
};
/**
 * [客户端使用]检查SSR状态，true为正常，false需要降级
 * 默认只检查用户身份信息
 * @param { TCheckSSRStatusOptions } options
 * @returns { Boolean }
 */
function checkSSRStatus(options) {
    // @ts-ignore 因为新版本 API 已不存在noCheck和extraUrlParamsCheck参数，所以忽略下一行 TS 校验
    const { noCheck = { loginInfo: false, urlParams: [] }, extraUrlParamsCheck = [] } = options ?? {};
    const isParamsRepeat = extraUrlParamsCheck.some(item => (noCheck.urlParams ?? []).includes(item));
    if (isParamsRepeat) {
        console.error('【checkSSRStatus】参数互斥, extraUrlParamsCheck与noCheck.urlParams不应存在交集');
        return false;
    }
    let { skipLoginCheck, customURLParams = [] } = options || {};
    // 兼容旧版本的 noCheck.loginInfo 参数
    if (utils.isObj(options) && ('noCheck' in options || 'extraUrlParamsCheck' in options)) {
        console.warn('[warn]checkSSRStatus API 已更新迭代，请使用新版本的 API 参数');
    }
    if (noCheck?.loginInfo === true) {
        skipLoginCheck = true;
    }
    // 升级新版本 API 后，默认不校验['xpsid', 'xoid', 'x_act_key'] URL参数了，所以不需要再兼容旧版本 noCheck.urlParams参数了。
    // 兼容旧版本 extraUrlParamsCheck 参数。
    if (Array.isArray(extraUrlParamsCheck) && extraUrlParamsCheck.length > 0) {
        customURLParams = extraUrlParamsCheck;
    }
    const storage = getStorage();
    const ssrQuery = storage.serverRenderResource.ssrQuery;
    if (ssrQuery === undefined)
        return false; // ssrQuery为「undefined」证明不是SSR，或者调用 initSDK 时未传递 serverCtx 参数
    const urlParams = Object.assign(query(), hashQuery());
    /* 埋点上报的公参 */
    const trackCommonParams = {
        x_act_key: ssrQuery.x_act_key,
        xpsid: ssrQuery.xpsid,
        s_xoid: ssrQuery.xoid,
        c_xoid: urlParams.xoid,
        prod_key: ssrQuery.prod_key,
        dchn: ssrQuery.dchn,
    };
    /* 需要被执行检查的集合 */
    const checkPipe = [
        {
            /* 校验urlParams */
            fn: checkUrlParams,
            value: customURLParams,
            exec: true
        },
        {
            /* 校验用户登录信息 */
            fn: checkLoginInfo,
            exec: !skipLoginCheck,
        }
    ];
    /* 得出是否存在校验不通过的结果 */
    const isSame = checkPipe.map(check => {
        const value = {
            trackCommonParams,
            urlParams,
            ssrQuery,
            storage,
            value: check.value
        };
        if (!check.exec)
            return true; // 不执行的函数默认为true，校验通过
        return check.fn(value);
    }).every(bool => bool);
    return isSame;
}
function checkUrlParams({ value: checkList, ssrQuery, urlParams, trackCommonParams }) {
    /* 记录ssrQuery缺失的key */
    const errmsg = [];
    /* 记录有diff的key */
    const diffQuery = [];
    checkList.forEach(key => {
        const isSame = ssrQuery[key] === urlParams[key];
        if (!isSame) {
            diffQuery.push(key);
            if (!ssrQuery[key]) {
                /* 如果ssrQuery不存在所校验的参数，认定缺失并推入集合 */
                errmsg.push(`SSR query ${key} is empty.`);
            }
        }
    });
    if (diffQuery.length) {
        trackError('SSRError', 'webx-js', EventTable.ERROR_CTX_URL_PARAMS_NOT_SAME.label, 0, {
            ...trackCommonParams,
            ssr_error_type: EventTable.ERROR_CTX_URL_PARAMS_NOT_SAME.ssrErrorType,
            ssrQuery: JSON.stringify(ssrQuery),
            urlParams: JSON.stringify(urlParams),
            diff_query: JSON.stringify(diffQuery),
            err_msg: errmsg.join('｜'),
        });
        return false;
    }
    return true;
}
function checkLoginInfo({ storage, ssrQuery, trackCommonParams }) {
    const serverUid = ssrQuery.uid && String(ssrQuery.uid);
    const serverToken = ssrQuery.token;
    const webUid = storage?.userInfo?.uid && String(storage?.userInfo?.uid);
    const webToken = storage?.userInfo?.token;
    if (serverUid && webUid) {
        if (serverUid === webUid) {
            return true;
        }
        else {
            // 上报 『uid 不一致』的自定义错误埋点
            trackError('SSRError', 'webx-js', EventTable.ERROR_CTX_SAME_USER_UID_NOT_SAME.label, 0, {
                ...trackCommonParams,
                ssr_error_type: EventTable.ERROR_CTX_SAME_USER_UID_NOT_SAME.ssrErrorType,
                serverUid,
                webUid,
            });
            return false;
        }
    }
    else if (serverToken && webToken) {
        // 上报『缺少 uid 』的自定义错误埋点
        trackError('SSRError', 'webx-js', EventTable.ERROR_MISSING_UID.label, 2, {
            ...trackCommonParams,
            ssr_error_type: EventTable.ERROR_MISSING_UID.ssrErrorType,
            serverUid,
            webUid,
        });
        if (serverToken === webToken) {
            return true;
        }
        else {
            // 上报 『token 不一致』的自定义错误埋点
            trackError('SSRError', 'webx-js', EventTable.ERROR_CTX_SAME_USER_TOKEN_NOT_SAME.label, 0, {
                ...trackCommonParams,
                ssr_error_type: EventTable.ERROR_CTX_SAME_USER_TOKEN_NOT_SAME.ssrErrorType,
                serverToken,
                webToken,
            });
            return false;
        }
    }
    else {
        // 上报『请确保调用前已完成登录』的自定义错误埋点
        trackError('SSRError', 'webx-js', EventTable.ERROR_PLEASE_ENSURE_HAS_LOGIN.label, 2, {
            ...trackCommonParams,
            ssr_error_type: EventTable.ERROR_PLEASE_ENSURE_HAS_LOGIN.ssrErrorType,
            serverUid,
            webUid,
            serverToken,
            webToken,
        });
        return false;
    }
}
export { checkSSRStatus };
